import * as React from 'react';
import {
  Button,
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  Text,
  tokens,
  makeStyles,
  shorthands,
} from '@fluentui/react-components';
import {
  Navigation20Regular,
  Dismiss20Regular,
  type FluentIcon,
} from '@fluentui/react-icons';

/**
 * A single action shown in the menu.
 * `onSelect` runs after the drawer has been closed.
 */
export interface ActionMenuItem {
  /** Stable key for React lists. */
  key: string;
  /** Visible label. */
  label: string;
  /** Fluent UI icon component, e.g. Phone20Regular. */
  icon?: FluentIcon;
  /** Handler — may be async. The menu closes itself before this runs. */
  onSelect: () => void | Promise<void>;
  /** Greys out and blocks the item. */
  disabled?: boolean;
  /** Set false to omit the item entirely (handy for role/feature gates). */
  visible?: boolean;
  /** Highlights the item as the active/current selection. */
  active?: boolean;
}

export interface ActionMenuProps {
  /** Drawer header title. */
  title: string;
  /** Drawer header subtitle. */
  description?: string;
  /** The action buttons. */
  items: ActionMenuItem[];
  /**
   * Optional content rendered above the actions (e.g. a theme toggle row).
   * Rendered as-is.
   */
  header?: React.ReactNode;
  /**
   * Optional content rendered below the actions, pinned to the bottom of the
   * drawer (e.g. a "View as" role switcher). Rendered as-is.
   */
  footer?: React.ReactNode;
  /** Accessible label / tooltip for the trigger button. */
  triggerLabel?: string;
  /** Side the panel slides in from. Defaults to "end" (right). */
  side?: 'start' | 'end';
  /** Optional right offset for the fixed trigger button. */
  triggerRight?: string;
}

const useStyles = makeStyles({
  triggerButton: {
    position: 'fixed',
    top: tokens.spacingVerticalM,
    right: tokens.spacingHorizontalM,
    zIndex: '50',
    boxShadow: tokens.shadow8,
    flexShrink: 0,
    borderRadius: tokens.borderRadiusXLarge,
  },
  drawer: {
    width: 'min(90vw, 22rem)',
    // Slide in from the right. Fluent's OverlayDrawer transitions `transform`,
    // so animate it explicitly for a smooth, predictable slide.
    transition: 'transform 0.25s cubic-bezier(0.1, 0.9, 0.2, 1)',
    willChange: 'transform',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalL,
    // Push the footer (e.g. "View as") to the bottom of the drawer.
    flexGrow: 1,
  },
  footer: {
    marginTop: 'auto',
    paddingTop: tokens.spacingVerticalM,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
  item: {
    width: '100%',
    justifyContent: 'flex-start',
    borderRadius: tokens.borderRadiusXLarge,
  },
  itemActive: {
    backgroundColor: tokens.colorBrandBackground2,
    fontWeight: tokens.fontWeightSemibold,
  },
  description: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

/**
 * A slide-in side panel triggered by a hamburger button. Self-contained:
 * owns its own open/close state. Built on the Fluent UI `OverlayDrawer`
 * (Radix-equivalent), so it gets focus trapping, Esc-to-close, and
 * outside-click dismissal for free.
 *
 * Mirrors the Voicemail Manager `ActionMenu` look & API, adapted to
 * QuickDial's Fluent UI component stack.
 */
export function ActionMenu({
  title,
  description,
  items,
  header,
  footer,
  triggerLabel = 'Menu',
  side = 'end',
  triggerRight,
}: ActionMenuProps) {
  const styles = useStyles();
  const [open, setOpen] = React.useState(false);

  const handleSelect = async (item: ActionMenuItem) => {
    // Close first so the panel doesn't linger during navigation / async work.
    setOpen(false);
    await item.onSelect();
  };

  return (
    <>
      <Button
        className={styles.triggerButton}
        style={triggerRight ? { right: triggerRight } : undefined}
        appearance="secondary"
        icon={<Navigation20Regular />}
        title={triggerLabel}
        aria-label={triggerLabel}
        onClick={() => setOpen(true)}
      />

      <OverlayDrawer
        className={styles.drawer}
        position={side}
        open={open}
        onOpenChange={(_, { open: isOpen }) => setOpen(isOpen)}
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close"
                icon={<Dismiss20Regular />}
                onClick={() => setOpen(false)}
              />
            }
          >
            {title}
          </DrawerHeaderTitle>
          {description ? (
            <Text className={styles.description}>{description}</Text>
          ) : null}
        </DrawerHeader>

        <DrawerBody className={styles.body}>
          {header}

          {items
            .filter((item) => item.visible !== false)
            .map((item) => {
              const Icon = item.icon;
              return (
                <Button
                  key={item.key}
                  appearance="outline"
                  className={
                    item.active
                      ? `${styles.item} ${styles.itemActive}`
                      : styles.item
                  }
                  disabled={item.disabled ?? false}
                  {...(Icon ? { icon: <Icon /> } : {})}
                  onClick={() => handleSelect(item)}
                >
                  {item.label}
                </Button>
              );
            })}

          {footer ? <div className={styles.footer}>{footer}</div> : null}
        </DrawerBody>
      </OverlayDrawer>
    </>
  );
}
